import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getSpaceConfig } from '../api/configApi';
import { SYSTEM_ROLES } from '../auth/permissions';

const SpaceConfigContext = createContext(null);

const ALL_OFF = {
  payments: false, events: false, advertisers: false, aiModeration: false,
  selfies: false, lighting: false, items: false, rewards: false,
  charities: false, subscriptions: false, recognition: false,
  booths: false, hosting: false, approvals: false,
};

export function SpaceConfigProvider({ children }) {
  const { activeSpace, isAuthenticated, signInError, user } = useAuth();
  const isGmsAccountant = user?.systemRole === SYSTEM_ROLES.GMS_ACCOUNTANT;
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (spaceId) => {
    if (!spaceId) { setConfig(null); setLoading(false); return; }
    setLoading(true);
    if (isGmsAccountant) {
      setConfig({ charities: true, advertisers: true, payments: true });
      setLoading(false);
      return;
    }
    try {
      const cfg = await getSpaceConfig(spaceId);
      setConfig(cfg);
    } catch {
      setConfig(ALL_OFF);
    } finally {
      setLoading(false);
    }
  }, [isGmsAccountant]);

  useEffect(() => {
    // signInError catches a dead session isAuthenticated alone misses (stale MSAL account).
    if (!isAuthenticated || signInError) { setConfig(null); setLoading(false); return; }
    const id = Number(activeSpace?.spaceId);
    load(Number.isInteger(id) && id > 0 ? id : null);
  }, [activeSpace?.spaceId, isAuthenticated, signInError, load]);

  return (
    <SpaceConfigContext.Provider value={{ config, setConfig, loading }}>
      {children}
    </SpaceConfigContext.Provider>
  );
}

export const useSpaceConfig = () => useContext(SpaceConfigContext);
